import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import ChangePasswordModal from '../ChangePasswordModal/ChangePasswordModal';
import AppIcon from '../AppIcon/AppIcon';
import styles from './Sidebar.module.css';

const Sidebar = ({ menuItems, role }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  // Items with `children` are dropdowns. Closed by default, open when one of their pages is
  // showing, until the user toggles them (the caret click, tracked per item path).
  const [openGroups, setOpenGroups] = useState({});

  const renderLink = (item, key, caret = null) => {
    // Exact match by default. An item that owns nested pages opts in with
    // `matchNested`, which also highlights it on <path>/...
    const isActive = location.pathname === item.path
      || (item.matchNested && location.pathname.startsWith(`${item.path}/`));

    return (
      <Link
        key={key}
        to={item.path}
        className={`${styles.navItem} ${isActive ? styles.active : ''} ${item.isSubItem ? styles.subItem : ''}`}
      >
        <span className={styles.icon}>
          <AppIcon name={item.icon} size={16} />
        </span>
        <span className={styles.label}>{item.label}</span>
        {caret}
      </Link>
    );
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.logo}>
        <h2>COSMIC</h2>
        <span className={styles.roleTag}>{role}</span>
      </div>
      
      <nav className={styles.nav}>
        {menuItems.map((item, index) => {
          if (item.type === 'header') {
            return (
              <div key={index} className={styles.navHeader}>
                {item.label}
              </div>
            );
          }

          if (item.children) {
            const isOpen = openGroups[item.path]
              ?? item.children.some(child => child.path === location.pathname);
            const caret = (
              <span
                className={styles.caret}
                onClick={(e) => {
                  e.preventDefault();
                  setOpenGroups(prev => ({ ...prev, [item.path]: !isOpen }));
                }}
              >
                {isOpen ? '▾' : '▸'}
              </span>
            );

            return (
              <React.Fragment key={index}>
                {renderLink(item, item.path, caret)}
                {isOpen && item.children.map(child => renderLink({ ...child, isSubItem: true }, child.path))}
              </React.Fragment>
            );
          }

          return renderLink(item, index);
        })}
      </nav>
      
      <div className={styles.footer}>
        <button 
          onClick={() => setIsPasswordModalOpen(true)} 
          className={styles.changePasswordBtn}
        >
          <span>
            <AppIcon name="password" size={14} />
          </span>
          Change Password
        </button>
        <button onClick={logout} className={styles.logoutBtn}>
          <span>
            <AppIcon name="logout" size={14} />
          </span>
          Logout
        </button>
      </div>

      <ChangePasswordModal 
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
};

export default Sidebar;
